import React, { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { TranslatableInput } from 'components/formInputs/tranlatableInput';
import { getValidationPairsForTranslatableText, getValidationShapeForTranslatableText, TranslationFieldOptions } from 'utils/schemaHelper';
import { makeId } from 'utils/stringHelper';
import { formFileUrlFromDbFile } from 'utils/imageHelper';
import { MenuCategoryImages } from './_category_images';

export const MenuCategory = forwardRef(({ itemData, initData, onRemove, onOrderUp, onOrderDown }, ref) => {
  const [categoryImages, setCategoryImages] = useState([]);

  const schema = yup.object().shape({
    category_id: yup.string().default(itemData?.category_id),
    name: yup
      .object()
      .shape(
        getValidationShapeForTranslatableText(
          itemData?.name,
          TranslationFieldOptions.REQUIRE_AT_LEAST_ONE,
        ),
        getValidationPairsForTranslatableText(),
      ),
  });

  const {
    register,
    formState: { errors },
    clearErrors,
    getValues,
    reset,
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (initData && itemData) {
      const itemDataImages = initData?.category_images?.filter((x) => x.category_id === itemData.category_id);
      const formedInit = itemDataImages.map((item) => {
        return {
          key: makeId(10),
          languages: item.languages,
          images: item.images ? item.images.map((x) => ({ data_url: formFileUrlFromDbFile(x, true), id: x.id })) : [],
        };
      });

      setCategoryImages(formedInit);
    }
  }, [initData]);

  useEffect(() => {
    reset(itemData);
  }, [itemData, reset]);

  useImperativeHandle(ref, () => ({
    getData() {
      return {
        category_id: getValues('category_id'),
        name: getValues('name'),
        images: categoryImages,
      };
    },
  }));

  const usedLanguages = [];

  categoryImages.forEach((item) => {
    if (item.languages) {
      item.languages.forEach((lang) => usedLanguages.push(lang));
    }
  });

  return (
    <div className="w-full grid grid-cols-6 gap-6">
      <div className="col-span-6 sm:col-span-3">
        <TranslatableInput
          name="name"
          register={register}
          error={errors?.name}
          containerClassName="sm:col-span-3"
          labelClassName="block text-sm font-medium text-gray-700"
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          placeholder="Category name*"
          onChange={() => {
            if (errors?.name) {
              clearErrors('name');
            }
          }}
        />
      </div>
      <div className="col-span-6 sm:col-span-3 self-end">
        <button
          type="button"
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md
            text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-2"
          onClick={() => {
            setCategoryImages([...(categoryImages || []), { key: makeId(10), languages: [], images: [] }]);
          }}
        >
          Add images
        </button>
        <button
          type="button"
          className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700
            hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mt-2"
          onClick={onRemove}
        >
          Remove
        </button>
        <div className="inline-block mt-2 ml-2">
          {!!onOrderUp && (
          <button
            type="button"
            className="mr-2"
            onClick={onOrderUp}
          >
            <ArrowUpIcon className="h-5 w-5 text-indigo-500" />
          </button>
          )}
          {!!onOrderDown && (
          <button
            type="button"
            className=""
            onClick={onOrderDown}
          >
            <ArrowDownIcon className="h-5 w-5 text-indigo-500" />
          </button>
          )}
        </div>
      </div>
      {categoryImages.map((item, ind) => {
        return (
          <div key={item.key} className="col-span-full">
            <div>
              <MenuCategoryImages
                id={item.key}
                usedLanguages={usedLanguages}
                languages={item.languages}
                images={item.images}
                onLanguageChange={(val) => {
                  const newVal = [...categoryImages];

                  newVal[ind].languages = val;
                  setCategoryImages(newVal);
                }}
                onImageChange={(val) => {
                  const newVal = [...categoryImages];

                  newVal[ind].images = val;
                  setCategoryImages(newVal);
                }}
              />
            </div>
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={() => {
                  categoryImages.splice(ind, 1);
                  setCategoryImages([...(categoryImages || [])]);
                }}
                className="text-sm text-red-500"
              >
                Remove option
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
});
